import { useState, type JSX } from "react";
import { toast } from "sonner";
import { Undo2 } from "lucide-react";
import { resolveLeftoverLabel, type Day, type MealType, type RecipeApiItem, type Slot } from "@recipevault/shared";
import { MEAL_LABELS } from "./mealLabels.js";
import { resolveItemLabel } from "./resolveItem.js";
import { toggleSlotSkipped } from "./editWeek.js";
import { friendlyError } from "../../shared/http.js";
import { SlotSheet } from "./SlotSheet.js";
import { RecipePhotoImg } from "../recipes/RecipePhotoImg.js";
import { CoverPlaceholder } from "../recipes/CoverPlaceholder.js";

interface DayViewProps {
  day: Day;
  isToday: boolean;
  recipeTitles: Map<string, string>;
  /** The route's week key — the real, addressable key `editWeek` lazy-creates against. */
  weekStart: string;
  /** The whole week's days, threaded through to `SlotSheet` for leftover/move pickers. */
  days: Day[];
  /** Every slot in the plan, indexed by id — resolves a leftover's real source label. */
  slotIndex: ReadonlyMap<string, Slot>;
  /** Recipe rows resolve their cover thumb from here — keyed by recipe id. */
  recipesById: Map<string, RecipeApiItem>;
  /** Required by `RecipePhotoImg` to compose the signed-cookie CDN URL. */
  householdId: string;
}

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: "UTC" });

/** Weekday name for an ISO calendar date, anchored at UTC noon (no DST edge). */
function weekdayLabel(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map((v) => Number.parseInt(v, 10)) as [number, number, number];
  return WEEKDAY_FORMATTER.format(new Date(Date.UTC(y, m - 1, d, 12)));
}

/**
 * One full day's meal slots, rendered directly — no accordion. This is the
 * enriched successor to `DaySection` (the day-pager's primary surface): every
 * slot is always visible, recipe rows carry a small cover thumb, and cooked
 * slots get a mini badge. Slot mutation plumbing (skip toggle, `SlotSheet`
 * wiring) is unchanged from `DaySection` — only presentation differs.
 */
export function DayView({
  day,
  isToday,
  recipeTitles,
  weekStart,
  days,
  slotIndex,
  recipesById,
  householdId,
}: DayViewProps): JSX.Element {
  const [activeMealType, setActiveMealType] = useState<MealType | null>(null);

  async function handleToggleSkip(mealType: MealType): Promise<void> {
    try {
      await toggleSlotSkipped(weekStart, day.date, mealType);
    } catch (e) {
      toast.error(friendlyError(e));
    }
  }

  const activeSlot = activeMealType ? day.slots.find((s) => s.mealType === activeMealType) : undefined;

  return (
    <section
      aria-label={day.date}
      className={`rounded-xl border p-3 ${
        isToday ? "border-emerald-700 bg-emerald-950/20" : "border-zinc-800 bg-zinc-900"
      }`}
    >
      <h2 className="flex items-center gap-2 text-sm font-semibold text-zinc-200">
        {weekdayLabel(day.date)} · {day.date}
        {isToday && (
          <span className="rounded-full bg-emerald-700 px-2 py-0.5 text-xs font-medium text-emerald-100">
            Today
          </span>
        )}
      </h2>

      <ul className="mt-2 flex flex-col gap-1.5">
        {day.slots.map((slot) => {
          const isEmpty = slot.items.length === 0 && slot.leftoverOf === undefined;
          const skipped = slot.skipped === true;
          const cooked = slot.cookedAt != null;
          const firstRecipe = slot.items.find((i) => i.kind === "recipe");
          const rec = firstRecipe ? recipesById.get(firstRecipe.recipeId) : undefined;

          return (
            <li key={slot.id} className="flex min-h-12 items-center justify-between gap-2 text-sm">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                {rec && rec.photos.coverKey ? (
                  <RecipePhotoImg
                    householdId={householdId}
                    recipeId={rec.id}
                    photoKey={rec.photos.coverKey}
                    size="thumb"
                    alt={`Cover for ${rec.title}`}
                    className="w-10 h-10 rounded-md object-cover shrink-0"
                  />
                ) : rec ? (
                  <CoverPlaceholder
                    seed={rec.id}
                    title={rec.title}
                    className="w-10 h-10 rounded-md shrink-0"
                    letterClassName="text-base"
                  />
                ) : null}

                <div className="flex min-w-0 flex-col">
                  <span className="text-xs text-zinc-500">{MEAL_LABELS[slot.mealType]}</span>
                  {skipped ? (
                    <button
                      type="button"
                      onClick={() => { void handleToggleSkip(slot.mealType); }}
                      className="w-fit text-left text-zinc-600 line-through hover:text-zinc-400"
                    >
                      Skipped
                    </button>
                  ) : isEmpty ? (
                    <button
                      type="button"
                      onClick={() => { setActiveMealType(slot.mealType); }}
                      className="w-fit text-left text-xs text-zinc-500 hover:text-zinc-300"
                    >
                      + Add
                    </button>
                  ) : (
                    <div className="flex min-w-0 items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => { setActiveMealType(slot.mealType); }}
                        className="flex min-w-0 items-center gap-1 truncate text-left text-zinc-200 hover:text-zinc-50"
                      >
                        {slot.leftoverOf ? (
                          <>
                            <Undo2 className="h-3.5 w-3.5 shrink-0 text-zinc-500" aria-hidden="true" />
                            {(() => {
                              const label = resolveLeftoverLabel(slot, slotIndex, {
                                recipeTitle: (id) => recipeTitles.get(id) ?? null,
                              });
                              return label === null || label === "Leftover"
                                ? "Leftover"
                                : `Leftover: ${label}`;
                            })()}
                          </>
                        ) : (
                          slot.items.map((item) => resolveItemLabel(item, recipeTitles)).join(", ")
                        )}
                      </button>
                      {/* calorie chip seam: render a muted ~N kcal chip here when a calories field ships (Slice B era) */}
                      {cooked && (
                        <span className="shrink-0 rounded-full bg-emerald-950/60 px-1.5 py-0.5 text-[0.6875rem] font-medium text-emerald-400">
                          ✓ cooked
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!skipped && (
                <button
                  type="button"
                  onClick={() => { void handleToggleSkip(slot.mealType); }}
                  className="shrink-0 rounded-full border border-zinc-700 px-1.5 py-0.5 text-[0.6875rem] text-zinc-600 hover:text-zinc-300"
                >
                  Skip
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {day.note && <p className="mt-2 text-xs text-zinc-500">{day.note}</p>}

      {activeMealType && activeSlot && (
        <SlotSheet
          open
          onOpenChange={(next) => {
            if (!next) setActiveMealType(null);
          }}
          weekStart={weekStart}
          date={day.date}
          mealType={activeMealType}
          slot={activeSlot}
          days={days}
          recipeTitles={recipeTitles}
        />
      )}
    </section>
  );
}
